/**
 * VoiceAgent — Reusable voice interaction component for AI assistants
 *
 * Built following the xAI Realtime API best practices:
 * - AudioWorklet (not deprecated ScriptProcessorNode)
 * - Mic buffering before session ready
 * - Chunked base64 encoding (no stack overflow)
 * - Parallel mic capture + WebSocket connect
 * - AudioContext warmup inside user gesture (Safari)
 * - Gapless playback with scheduled AudioBufferSourceNodes
 * - Automatic interruption on speech_started
 *
 * Props:
 *   agent     — Agent ID string (e.g. 'chat', 'coach', 'case-analyst', 'cps-advisor', 'orchestrate')
 *   onTranscript(text, role) — Called when transcript text arrives ('user' or 'assistant')
 *   onStateChange(state)     — Called when voice state changes
 *   className — Extra classes for the container
 *
 * Voice states: 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking'
 */
import { useState, useRef, useCallback, useEffect } from 'react'
import { getToken } from '@/utils/auth'

const SAMPLE_RATE = 24000
const MIC_BUFFER_CAP = SAMPLE_RATE * 10 // ~10 seconds safety cap

// ── Chunked base64 encoding (avoids stack overflow on large buffers) ─────────
function audioToBase64(int16Array) {
  const bytes = new Uint8Array(int16Array.buffer, int16Array.byteOffset, int16Array.byteLength)
  const CHUNK = 0x2000 // 8 KiB chunks
  const parts = []
  for (let i = 0; i < bytes.length; i += CHUNK) {
    parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK)))
  }
  return btoa(parts.join(''))
}

// ── Decode base64 PCM to Float32 for playback ───────────────────────────────
function base64PcmToFloat32(base64) {
  const raw = atob(base64)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  const int16 = new Int16Array(bytes.buffer)
  const float32 = new Float32Array(int16.length)
  for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768
  return float32
}

// ── Component ────────────────────────────────────────────────────────────────
export default function VoiceAgent({ agent = 'chat', onTranscript, onStateChange, className = '' }) {
  const [state, setState]           = useState('idle')
  const [error, setError]           = useState(null)
  const [transcript, setTranscript] = useState('')

  const wsRef            = useRef(null)
  const streamRef        = useRef(null)
  const audioCtxRef      = useRef(null)
  const workletNodeRef   = useRef(null)
  const micBufferRef     = useRef([])      // Buffer mic chunks until session ready
  const micBufferSamples = useRef(0)
  const isSessionReady   = useRef(false)
  const intentionalClose = useRef(false)

  // Playback state
  const queuedSourcesRef = useRef([])
  const nextPlayTimeRef  = useRef(0)

  const setVoiceState = useCallback((s) => {
    setState(s)
    onStateChange?.(s)
  }, [onStateChange])

  // ── Gapless audio playback ──────────────────────────────────────────────
  const playPcmChunk = useCallback((base64) => {
    const ctx = audioCtxRef.current
    if (!ctx) return

    const float32 = base64PcmToFloat32(base64)
    const buf = ctx.createBuffer(1, float32.length, SAMPLE_RATE)
    buf.getChannelData(0).set(float32)

    const src = ctx.createBufferSource()
    src.buffer = buf
    src.connect(ctx.destination)

    const now = ctx.currentTime
    const startAt = Math.max(now, nextPlayTimeRef.current)
    src.start(startAt)
    nextPlayTimeRef.current = startAt + buf.duration

    queuedSourcesRef.current.push(src)
    src.onended = () => {
      const idx = queuedSourcesRef.current.indexOf(src)
      if (idx !== -1) queuedSourcesRef.current.splice(idx, 1)
    }
  }, [])

  // ── Interrupt playback ──────────────────────────────────────────────────
  const interruptPlayback = useCallback(() => {
    for (const src of queuedSourcesRef.current) {
      try { src.stop() } catch {}
    }
    queuedSourcesRef.current.length = 0
    nextPlayTimeRef.current = 0
  }, [])

  // ── Stop everything ─────────────────────────────────────────────────────
  const stop = useCallback(() => {
    intentionalClose.current = true

    // Close WebSocket
    if (wsRef.current) {
      try { wsRef.current.close() } catch {}
      wsRef.current = null
    }

    // Stop microphone tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }

    // Disconnect worklet
    if (workletNodeRef.current) {
      try { workletNodeRef.current.disconnect() } catch {}
      workletNodeRef.current = null
    }

    // Stop playback
    interruptPlayback()

    // Close AudioContext
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close() } catch {}
      audioCtxRef.current = null
    }

    // Reset buffers
    micBufferRef.current = []
    micBufferSamples.current = 0
    isSessionReady.current = false

    setVoiceState('idle')
    setTranscript('')
  }, [setVoiceState, interruptPlayback])

  // Clean up on unmount
  useEffect(() => stop, [stop])

  // ── Start voice session ─────────────────────────────────────────────────
  const start = useCallback(async () => {
    setError(null)
    setTranscript('')
    intentionalClose.current = false
    isSessionReady.current = false
    micBufferRef.current = []
    micBufferSamples.current = 0

    // Show connecting immediately (mic button flips to active on tap per UX guidelines)
    setVoiceState('connecting')

    // Get auth token
    let token = ''
    try { token = await getToken() } catch {}

    // ── CRITICAL: Create AudioContext inside user gesture for Safari ──────
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: SAMPLE_RATE })
    if (audioCtx.state === 'suspended') await audioCtx.resume()
    audioCtxRef.current = audioCtx

    // ── Start mic capture immediately (parallel with WebSocket connect) ──
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: SAMPLE_RATE,
        },
      })
      streamRef.current = stream
    } catch (e) {
      const msg = e.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow mic access.'
        : e.name === 'NotFoundError'
        ? 'No microphone found.'
        : 'Microphone error. Please try again.'
      setError(msg)
      setVoiceState('idle')
      return
    }

    // Detect mic disconnection
    stream.getAudioTracks()[0]?.addEventListener('ended', () => {
      if (!intentionalClose.current) {
        setError('Microphone disconnected.')
        stop()
      }
    })

    // ── Set up AudioWorklet for mic capture ──────────────────────────────
    try {
      await audioCtx.audioWorklet.addModule('/pcm-processor-worklet.js')
    } catch {
      // Fallback: try with relative path for dev server
      try {
        await audioCtx.audioWorklet.addModule('./pcm-processor-worklet.js')
      } catch (e2) {
        setError('Audio processor failed to load.')
        setVoiceState('idle')
        return
      }
    }

    const source = audioCtx.createMediaStreamSource(stream)
    const workletNode = new AudioWorkletNode(audioCtx, 'pcm-processor')
    workletNodeRef.current = workletNode

    // Handle mic data: buffer until session ready, then stream live
    workletNode.port.onmessage = (event) => {
      const int16Data = event.data // Int16Array from worklet

      if (isSessionReady.current && wsRef.current?.readyState === WebSocket.OPEN) {
        // Live streaming
        wsRef.current.send(JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: audioToBase64(int16Data),
        }))
      } else {
        // Buffer until session ready (with safety cap)
        if (micBufferSamples.current < MIC_BUFFER_CAP) {
          micBufferRef.current.push(int16Data)
          micBufferSamples.current += int16Data.length
        }
      }
    }

    source.connect(workletNode)
    // AudioWorklet doesn't need to connect to destination (unlike ScriptProcessor)

    // ── Connect WebSocket in parallel ────────────────────────────────────
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${proto}//${window.location.host}/api/voice-proxy?agent=${encodeURIComponent(agent)}&token=${encodeURIComponent(token)}`

    // Connection timeout
    const connectTimeout = setTimeout(() => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        setError('Connection timed out. Try again.')
        stop()
      }
    }, 10000)

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      clearTimeout(connectTimeout)
      setVoiceState('listening')
    }

    ws.onmessage = ({ data }) => {
      let msg
      try { msg = JSON.parse(data) } catch { return }

      switch (msg.type) {
        case 'session.updated':
          // Session config acknowledged — flush mic buffer and go live
          if (!isSessionReady.current) {
            isSessionReady.current = true
            for (const chunk of micBufferRef.current) {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({
                  type: 'input_audio_buffer.append',
                  audio: audioToBase64(chunk),
                }))
              }
            }
            micBufferRef.current = []
            micBufferSamples.current = 0
          }
          break

        case 'session.created':
          // Session ready — wait for session.updated before sending audio
          break

        case 'input_audio_buffer.speech_started':
          // User started speaking — INTERRUPT playback automatically
          interruptPlayback()
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'response.cancel' }))
          }
          setVoiceState('listening')
          setTranscript('')
          break

        case 'input_audio_buffer.speech_stopped':
          // User stopped — AI is thinking
          setVoiceState('thinking')
          break

        case 'response.output_audio.delta':
        case 'response.audio.delta':
          // Audio chunk from assistant
          setVoiceState('speaking')
          playPcmChunk(msg.delta)
          break

        case 'response.output_audio_transcript.delta':
          // Streaming transcript of assistant speech
          setTranscript(t => t + (msg.delta || ''))
          break

        case 'response.output_audio_transcript.done':
          // Full assistant transcript complete
          if (msg.transcript) {
            onTranscript?.(msg.transcript, 'assistant')
          }
          setTranscript('')
          break

        case 'conversation.item.input_audio_transcription.completed':
          // User speech transcribed
          if (msg.transcript) {
            onTranscript?.(msg.transcript, 'user')
          }
          break

        case 'response.done':
          // Response finished — go back to listening
          setVoiceState('listening')
          break

        case 'error':
          setError(msg.error?.message || msg.message || 'Voice connection error')
          stop()
          break
      }
    }

    ws.onerror = () => {
      clearTimeout(connectTimeout)
      if (!intentionalClose.current) {
        setError('Voice connection failed. Try again.')
        stop()
      }
    }

    ws.onclose = () => {
      clearTimeout(connectTimeout)
      if (!intentionalClose.current) {
        setVoiceState('idle')
      }
    }
  }, [agent, onTranscript, stop, setVoiceState, interruptPlayback, playPcmChunk])

  // ── Toggle ──────────────────────────────────────────────────────────────
  const toggle = useCallback(() => {
    if (state === 'idle') start()
    else stop()
  }, [state, start, stop])

  // ── Render ──────────────────────────────────────────────────────────────
  const isActive = state !== 'idle'
  const stateLabel = {
    idle: 'Tap to speak',
    connecting: 'Connecting…',
    listening: 'Listening…',
    thinking: 'Thinking…',
    speaking: 'Speaking…',
  }[state]

  const pulseClass = state === 'listening' ? 'animate-pulse' :
                     state === 'speaking' ? 'animate-bounce' : ''

  return (
    <div className={`flex flex-col items-center gap-1.5 ${className}`}>
      {/* Mic button */}
      <button
        onClick={toggle}
        className={[
          'relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300',
          isActive
            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-110'
            : 'bg-brand-700 text-white shadow-md active:scale-95',
          pulseClass,
        ].join(' ')}
        aria-label={isActive ? 'Stop voice' : 'Start voice'}
      >
        {/* Pulse ring when active */}
        {isActive && (
          <span className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-40" />
        )}

        {isActive ? (
          // Stop icon
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          // Mic icon
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M19 10v2a7 7 0 01-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" strokeLinecap="round" />
            <line x1="8" y1="23" x2="16" y2="23" strokeLinecap="round" />
          </svg>
        )}
      </button>

      {/* State label */}
      <p className={`text-[10px] font-semibold transition-colors ${
        isActive ? 'text-red-500' : 'text-slate-400'
      }`}>
        {stateLabel}
      </p>

      {/* Live transcript tooltip */}
      {transcript && (
        <div className="absolute bottom-full mb-2 left-0 right-0 mx-4">
          <div className="bg-brand-700 text-white text-xs px-3 py-2 rounded-xl shadow-lg max-w-[200px] mx-auto text-center">
            {transcript}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-red-500 text-[10px] text-center max-w-[140px]">{error}</p>
      )}
    </div>
  )
}
